import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { usePrevious } from '@jira/platform__utils-react-hooks-use-previous/src/common/utils/index.tsx';
import { calculateContentBounds } from './calculateContentBounds.tsx';
import { calculateDefaultGlobalLoopsContainerPosition } from './calculateDefaultGlobalLoopsContainerPosition.tsx';
import type { ContentBounds } from './clampPan.tsx';
import { GLOBAL_LOOPS_CONTAINER_TYPE } from './directionKeys.tsx';
import {
	autoFitCanvas,
	findDefaultPositions,
	deferredExecute,
	definePortsOnDirectTransition,
	definePortsOnSharedTransition,
} from './getVerticalOffset.tsx';
import {
	isDirectOrSharedTransitionWithDefinedPorts,
	isNilOrEmpty,
} from './isEveryPropertyDefined.tsx';
import type { Positionable } from './ListEdgeDetails.tsx';
import type {
	DiagramStatus,
	Point,
	DiagramTransition,
	DiagramSharedTransitionWithDefinedPorts,
	DiagramDirectTransitionWithDefinedPorts,
} from './toPortInfo.tsx';
import { useLatest } from './useLatest.tsx';

export interface Options {
	transitions: DiagramTransition[];
	startNodePosition: Point | undefined;
	statuses: DiagramStatus[];
	statusWidths: Record<string, number>;
	isStatusWidthsReady: boolean;
	rootSvgRef: MutableRefObject<HTMLElement | null>;
	canvasContainerRef: MutableRefObject<HTMLElement | null>;
	providedGlobalLoopsContainerPosition: Point | undefined;
	onTransitionPortChange?: (transition: DiagramTransition) => void;
	onItemMove?: (payload: Positionable) => void;
	onPan: (point: Point) => void;
	onZoom: (zoom: number) => void;
	onClearGlobalLoopsContainerPosition: () => void;
	globalLoopedTransitions: DiagramTransition[];
}

export type TransitionsWithDefaultPorts = Record<
	string,
	DiagramDirectTransitionWithDefinedPorts | DiagramSharedTransitionWithDefinedPorts
>;

/** Handles assigning ports to transitions and status positions if they are not provided.
 * Currently this mimicks the existing behaviour of the old draw2d workflow diagram.
 *
 * Transition ports are determined using the angles between the connected ports.
 * Statuses are progressively placed in the middle right of the existing diagram elements.
 *
 */
export const useAutoLayout = ({
	transitions,
	startNodePosition,
	statuses,
	statusWidths,
	isStatusWidthsReady,
	rootSvgRef,
	canvasContainerRef,
	providedGlobalLoopsContainerPosition,
	onClearGlobalLoopsContainerPosition,
	onTransitionPortChange,
	onItemMove,
	onPan,
	onZoom,
	globalLoopedTransitions,
}: Options): {
	readonly isLayoutStable: boolean;
	readonly defaultPositions: Record<string, Point>;
	readonly defaultGlobalLoopsContainerPosition: Point | undefined;
	readonly transitionsWithDefaultPorts: TransitionsWithDefaultPorts;
	readonly contentBounds: ContentBounds | undefined;
} => {
	const latestStatuses = useLatest(statuses);
	const latestStatusWidths = useLatest(statusWidths);
	const hasInitialised = useRef<'pending' | 'started' | 'finished'>('pending');
	const [isLayoutStable, setIsLayoutStable] = useState(false);
	const doesDiagramContainGlobalLoops = transitions.some((x) => x.type === 'global-looped');
	const previousDoesDiagramContainGlobalLoops = usePrevious(doesDiagramContainGlobalLoops);

	const [defaultPositions, setDefaultPositions] = useState<Record<string, Point>>({});
	const [defaultGlobalLoopsContainerPosition, setDefaultGlobalLoopsContainerPosition] = useState<
		Point | undefined
	>();
	const [shouldAutoFit, setShouldAutoFit] = useState(false);

	const [transitionsWithDefaultPorts, setTransitionsWithDefaultPorts] =
		useState<TransitionsWithDefaultPorts>({});

	const latestDefaultPositions = useLatest(defaultPositions);
	const latestDefaultGlobalLoopsContainerPosition = useLatest(defaultGlobalLoopsContainerPosition);
	const latestStartNodePosition = useLatest(startNodePosition);
	const latestOnItemMove = useLatest(onItemMove);

	const contentBounds = expVal('hix-8229-dynamic-zoom-experiment', 'isEnabled', false)
		? calculateContentBounds({
				statuses,
				statusWidths,
				startNodePosition,
				globalLoopsContainerPosition:
					providedGlobalLoopsContainerPosition ?? defaultGlobalLoopsContainerPosition,
				globalLoopedTransitionsCount: globalLoopedTransitions.length,
				padding: 100,
				defaultPositions,
			})
		: undefined;
	const latestContentBounds = useLatest(contentBounds);

	const autoFit = useCallback(() => {
		// Only use state-based content bounds when experiment is enabled;
		// control group falls back to DOM measurement (old behavior)
		const boundsForAutoFit = expVal('hix-8229-dynamic-zoom-experiment', 'isEnabled', false)
			? latestContentBounds.current
			: undefined;
		autoFitCanvas(rootSvgRef, canvasContainerRef, onPan, onZoom, boundsForAutoFit);

		deferredExecute(() => {
			setIsLayoutStable(true);
		});
	}, [canvasContainerRef, onPan, onZoom, rootSvgRef, latestContentBounds]);

	// oxlint-disable-next-line react/react-compiler
	const autoPositionStatuses = useCallback(() => {
		const positioned = findDefaultPositions(
			latestStatusWidths.current,
			latestStartNodePosition.current,
			statuses,
			latestDefaultPositions.current,
		);

		if (positioned.length) {
			const newPositions = positioned.reduce<Record<string, Point>>((acc, positionable) => {
				acc[positionable.id] = positionable.position;
				return acc;
			}, {});

			setDefaultPositions((prev) => ({ ...prev, ...newPositions }));

			positioned.forEach((positionable) => {
				latestOnItemMove.current?.(positionable);
			});
		}

		return positioned;
	}, [
		latestDefaultPositions,
		latestOnItemMove,
		latestStatusWidths,
		latestStartNodePosition,
		statuses,
	]);

	useEffect(() => {
		// Effect to handle auto positioning of global loops container when global loop container is added or removed from diagram
		if (doesDiagramContainGlobalLoops) {
			if (!isNilOrEmpty(latestDefaultGlobalLoopsContainerPosition.current)) {
				// Exit early if global loops container already has a default position set
				return;
			}

			const defaultPosition = !isNilOrEmpty(providedGlobalLoopsContainerPosition)
				? providedGlobalLoopsContainerPosition
				: calculateDefaultGlobalLoopsContainerPosition(
						startNodePosition,
						statuses,
						defaultPositions,
					);

			// oxlint-disable-next-line react/react-compiler
			setDefaultGlobalLoopsContainerPosition(defaultPosition);

			// Call container move callback to save the calculated default back to the component that mounts the accessible diagram
			defaultPosition &&
				latestOnItemMove.current?.({
					id: GLOBAL_LOOPS_CONTAINER_TYPE,
					type: GLOBAL_LOOPS_CONTAINER_TYPE,
					position: defaultPosition,
				});
		} else if (previousDoesDiagramContainGlobalLoops) {
			// When global loops are removed from the diagram, clear out the global loops position so a new auto position can be calculated when it is added back
			setDefaultGlobalLoopsContainerPosition(undefined);
			onClearGlobalLoopsContainerPosition();
		}
	}, [
		doesDiagramContainGlobalLoops,
		previousDoesDiagramContainGlobalLoops,
		latestDefaultGlobalLoopsContainerPosition,
		providedGlobalLoopsContainerPosition,
		startNodePosition,
		statuses,
		defaultPositions,
		latestOnItemMove,
		onClearGlobalLoopsContainerPosition,
	]);

	useEffect(() => {
		// On initialisation - auto position statuses after timeout so that status widths are available and always autofit
		if (hasInitialised.current === 'pending') {
			hasInitialised.current = 'started';
		}

		if (hasInitialised.current === 'started' && isStatusWidthsReady) {
			autoPositionStatuses();
			setShouldAutoFit(true);
			hasInitialised.current = 'finished';
		}

		// Subsequent additional statuses - auto position statuses and auto fit if status has been positioned
		if (hasInitialised.current === 'finished') {
			const positioned = autoPositionStatuses();
			if (positioned.length) {
				// oxlint-disable-next-line react/react-compiler
				setShouldAutoFit(true);
			}
		}
	}, [
		autoPositionStatuses,
		latestDefaultPositions,
		latestOnItemMove,
		latestStatusWidths,
		startNodePosition,
		statuses,
		isStatusWidthsReady,
	]);

	useEffect(() => {
		// autofit after next render so that auto positioned statuses are in place
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (shouldAutoFit) {
			autoFit();
			// oxlint-disable-next-line react/react-compiler
			setShouldAutoFit(false);
		}
	}, [autoFit, shouldAutoFit]);

	/** Update transitions with undefined ports */
	useEffect(() => {
		const updatedTransitions: TransitionsWithDefaultPorts = {};

		const updateTransition = (
			transition: DiagramSharedTransitionWithDefinedPorts | DiagramDirectTransitionWithDefinedPorts,
		) => {
			updatedTransitions[transition.id] = transition;
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onTransitionPortChange?.(transition);
		};

		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		transitions.forEach((transition) => {
			if (
				transition.type === 'global-looped' ||
				transition.type === 'global' ||
				isDirectOrSharedTransitionWithDefinedPorts(transition)
			) {
				return;
			}

			if (transition.type === 'shared') {
				const positioned = definePortsOnSharedTransition(transition, latestStatuses.current);

				updateTransition(positioned);

				return;
			}

			const positioned = definePortsOnDirectTransition(transition, latestStatuses.current);

			updateTransition(positioned);
		});

		// oxlint-disable-next-line react/react-compiler
		setTransitionsWithDefaultPorts(updatedTransitions);
	}, [transitions, latestStatuses, onTransitionPortChange]);

	return {
		isLayoutStable,
		defaultPositions,
		defaultGlobalLoopsContainerPosition,
		transitionsWithDefaultPorts,
		contentBounds,
	} as const;
};
